import React, { useState, useEffect, useCallback, useContext, createContext } from "react";
import CertificateTypesPicker from "../../components/certificates/CertificateTypesPicker";
import {
  Box, Typography, CircularProgress, Alert, Button, Snackbar, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip, TextField,
} from "@mui/material";
import { useTheme, alpha, darken } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchSkillSheetBank, createSkillSheetBankItem, updateSkillSheetBankItem, deleteSkillSheetBankItem, publishSkillSheetBankItem,
  fetchTestBank, createTestBankItem, updateTestBankItem, deleteTestBankItem, publishTestBankItem,
  fetchDocumentBank, createDocumentBankItem, deleteDocumentBankItem, publishDocumentBankItem,
  fetchClassTemplates, instantiateClassTemplate, publishClassTemplate,
  publishToMarketplace, updateMarketplaceListing, withdrawFromMarketplace, deleteClassTemplate,
  unpublishSkillSheetBankItem, duplicateSkillSheetBankItem, unpublishTestBankItem, duplicateTestBankItem,
  unpublishDocumentBankItem, duplicateDocumentBankItem, unpublishClassTemplate, duplicateClassTemplate, updateClassTemplate, fetchTemplateCertificateTypes, saveTemplateCertificateTypes,
} from "../../api/backend";
import SkillSheetBuilderModal from "../../components/SkillSheetBuilderModal";
import { itemPoints } from "../../components/checklistScoring";
import CreateTestModal from "../../components/CreateTestModal";
import AddClassContentModal from "../../components/AddClassContentModal";
import ImportWizard from "../../components/knowledgeBank/importWizard/ImportWizard";
import MarketplaceListingDialog from "../../components/MarketplaceListingDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useTenant } from "../../contexts/TenantContext";
import { useActiveTenant } from "../../hooks/useActiveTenant";
import NoTenantSelected from "../../components/NoTenantSelected";
import StandardCoursesTab from "./standardCourses/StandardCoursesTab";
import KbCatalog, { CategoryPill, RowFigure } from "../../components/knowledgeBank/catalog/KbCatalog";

// ── Design tokens ─────────────────────────────────────────────────────────────
const D = {
  ink: "var(--color-ink)", ink2: "rgba(31,22,51,0.75)", ink3: "rgba(31,22,51,0.6)", ink4: "rgba(31,22,51,0.5)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-hairline-cloud)", line3: "var(--color-hairline-cloud)",
  surface: "var(--color-surface-canvas-light)", surface2: "var(--color-surface-press-light)", surface3: "var(--color-surface-press-stronger)",
  primary: "var(--color-primary)", primary50: "color-mix(in srgb, var(--color-primary) 10%, transparent)", primary10: "color-mix(in srgb, var(--color-primary) 6%, transparent)", primary700: "color-mix(in srgb, var(--color-primary) 85%, black)",
  green: "var(--color-success)", green50: "var(--color-success-bg)", green100: "var(--color-success-bg)",
  amber: "var(--color-warn)", amber50: "var(--color-warn-bg)", amber100: "var(--color-warn-bg)",
  live: "#c23d68", live50: "color-mix(in srgb, var(--color-accent-pink) 10%, transparent)", live100: "color-mix(in srgb, var(--color-accent-pink) 22%, transparent)",
  danger: "var(--color-danger)", danger50: "var(--color-danger-bg)", danger100: "var(--color-danger-bg)",
  blue: "var(--color-primary)", blue50: "rgba(37,99,235,.10)", blue100: "rgba(37,99,235,.18)",
  purple: "var(--color-accent-violet-deep)", purple50: "color-mix(in srgb, var(--color-accent-violet-deep) 10%, transparent)", purple100: "color-mix(in srgb, var(--color-accent-violet-deep) 20%, transparent)",
  shadowSm: "var(--shadow-card-sm)",
  shadowMd: "var(--shadow-card-md)",
};

// Tenant-branded primary — shadows the static D above with the theme's actual
// primary color so this page matches the branded look of the other pages.
function useD() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return {
    ...D,
    primary,
    primary700: darken(primary, 0.2),
    primary50: alpha(primary, 0.1),
    primary10: alpha(primary, 0.06),
  };
}

// ── Category → icon color ────────────────────────────────────────────────────
const FT_COLOR = {
  pdf:  { bg: D.danger50, color: D.danger },
  docx: { bg: D.blue50, color: D.blue },
  xlsx: { bg: D.green50, color: D.green },
  pptx: { bg: D.live50, color: D.live },
};


function parseTags(raw = "[]") {
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return raw.split(",").map(s => s.trim()).filter(Boolean); }
}

function parseCount(raw = "[]") {
  try { const v = typeof raw === "string" ? JSON.parse(raw) : raw; return Array.isArray(v) ? v.length : 0; } catch { return 0; }
}

function calcTotalPoints(itemsJson) {
  try {
    const data = typeof itemsJson === "string" ? JSON.parse(itemsJson) : itemsJson;
    if (!Array.isArray(data)) return null;
    // Handle sectioned items (sections with items array) or flat list
    let items = data;
    if (data.length > 0 && data[0]?.items) {
      items = data.flatMap((s) => (Array.isArray(s.items) ? s.items : []));
    }
    return items.reduce((sum, item) => sum + itemPoints(item), 0);
  } catch {
    return null;
  }
}

function fmtSize(bytes) {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtTime(min) {
  if (!min) return "No limit";
  if (min < 60) return `${min} min`;
  return `${(min / 60).toFixed(min % 60 === 0 ? 0 : 1)} hr`;
}

function ftFromFileName(name, contentType) {
  if (!name && !contentType) return null;
  const ext = name?.split(".").pop().toLowerCase();
  if (["pdf"].includes(ext) || contentType?.includes("pdf")) return "pdf";
  if (["doc", "docx"].includes(ext)) return "docx";
  if (["xls", "xlsx"].includes(ext)) return "xlsx";
  if (["ppt", "pptx"].includes(ext)) return "pptx";
  return null;
}

// ── SVG icons ─────────────────────────────────────────────────────────────────
function Ico({ size = 15, sw = 2, children, style }) {
  return <Box component="svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={style}>{children}</Box>;
}
const IcoPlus    = () => <Ico size={14} sw={2.5}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></Ico>;
const IcoEdit    = () => <Ico size={13}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></Ico>;
const IcoTrash   = () => <Ico size={13}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></Ico>;
const IcoEye     = () => <Ico size={13}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></Ico>;
const IcoUpload  = () => <Ico size={14}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></Ico>;
const IcoSkill   = () => <Ico size={20}><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></Ico>;
const IcoTest    = () => <Ico size={20}><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></Ico>;
const IcoDoc     = () => <Ico size={20}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></Ico>;
const IcoClock   = () => <Ico size={13}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></Ico>;
const IcoCourse  = () => <Ico size={11}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/></Ico>;
const IcoSteps   = () => <Ico size={13}><path d="M9 11l3 3L22 4"/></Ico>;

// ── Shared primitives ─────────────────────────────────────────────────────────
function Btn({ children, primary, ghost, sm, onClick, disabled, icon }) {
  const D = useD();
  const h = sm ? 36 : 42; const px = sm ? "14px" : "18px"; const fs = sm ? "13px" : "13.5px";
  const base = { height: h, px, borderRadius: sm ? "9px" : "11px", border: 0, fontFamily: "inherit", fontSize: fs, fontWeight: 700, cursor: disabled ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: "8px", transition: "all .13s", whiteSpace: "nowrap" };
  const v = primary
    ? { bgcolor: disabled ? D.surface3 : D.primary, color: disabled ? D.ink3 : "var(--color-surface-canvas-light)", boxShadow: disabled ? "none" : `0 4px 12px -4px ${alpha(D.primary, 0.45)}`, "&:hover:not(:disabled)": { bgcolor: D.primary700 } }
    : { bgcolor: "transparent", color: D.ink3, border: `1.5px solid ${D.line}`, "&:hover": { bgcolor: D.surface3, color: D.ink } };
  return <Box component="button" onClick={onClick} disabled={disabled} sx={{ ...base, ...v }}>{icon}{children}</Box>;
}

function IconBtn({ onClick, danger, children, title, "aria-label": ariaLabel }) {
  const btn = (
    <Box component="button" aria-label={ariaLabel || title} onClick={onClick} sx={{ width: 30, height: 30, borderRadius: "8px", border: `1.5px solid ${D.line}`, bgcolor: "var(--color-surface-canvas-light)", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink3, transition: "all .12s", flexShrink: 0, "&:hover": danger ? { bgcolor: D.danger50, color: D.danger, borderColor: D.danger100 } : { bgcolor: D.surface3, color: D.ink, borderColor: "var(--color-hairline-cool)" } }}>
      {children}
    </Box>
  );
  return title ? <Tooltip title={title} describeChild arrow>{btn}</Tooltip> : btn;
}


function MetaItem({ icon, children }) {
  return <Box sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12.5px", fontWeight: 600, color: D.ink2 }}><Box sx={{ color: D.ink4, display: "flex" }}>{icon}</Box>{children}</Box>;
}

function FtBadge({ ft }) {
  if (!ft) return null;
  const { bg, color } = FT_COLOR[ft] || { bg: D.surface3, color: D.ink2 };
  return <Box component="span" sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".05em", px: "8px", py: "3px", borderRadius: "6px", fontFamily: "'JetBrains Mono', 'Fira Code', monospace", bgcolor: bg, color, textTransform: "uppercase" }}>{ft}</Box>;
}

function MarketplaceBadge() {
  const D = useD();
  return <Box component="span" sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".05em", px: "8px", py: "3px", borderRadius: "6px", bgcolor: D.primary50, color: D.primary, textTransform: "uppercase" }}>On marketplace</Box>;
}

function PublishedBadge() {
  const D = useD();
  return <Box component="span" sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".05em", px: "8px", py: "3px", borderRadius: "6px", bgcolor: D.green50, color: D.green, textTransform: "uppercase" }}>Published</Box>;
}

function UpdateAvailableBadge() {
  const D = useD();
  return <Box component="span" sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".05em", px: "8px", py: "3px", borderRadius: "6px", border: `1.5px solid ${D.amber}`, color: D.amber, textTransform: "uppercase" }}>Update available</Box>;
}

/** Where an item stands: Draft / Published, on the marketplace, and whether the listing is behind. */
function StatusChips({ item, onView }) {
  return (
    <>
      {item.status === "DRAFT" ? <DraftBadge /> : <PublishedBadge />}
      {item.marketplaceListingId && <MarketplaceBadge />}
      {item.marketplaceListingId && item.marketplaceUpdateAvailable && (
        <Tooltip title="You've changed this since you last listed it. Use the Marketplace menu, then Update listing." describeChild arrow><span><UpdateAvailableBadge /></span></Tooltip>
      )}
      {item.importedFromListingId && item.marketplaceUpdateAvailableFromSource && (
        <Tooltip title="The marketplace has a newer version than your copy. Click to review and update." describeChild arrow>
          <Box component="button" onClick={(e) => { e.stopPropagation(); onView && onView(item); }} sx={{ border: 0, p: 0, bgcolor: "transparent", cursor: "pointer" }}>
            <MarketplaceUpdateBadge />
          </Box>
        </Tooltip>
      )}
    </>
  );
}

function MarketplaceUpdateBadge() {
  const D = useD();
  return <Box component="span" sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".05em", px: "8px", py: "3px", borderRadius: "6px", bgcolor: D.amber50, color: D.amber, border: `1.5px solid ${D.amber}`, textTransform: "uppercase" }}>Marketplace update available</Box>;
}

const ORIGIN_LABEL = { MANUAL: "Created manually", CLASS: "From class content", MARKETPLACE: "From marketplace" };

/** The marketplace listing this item is tied to: the one it was imported from, else the one it was listed as. */
function marketplaceLink(item) {
  if (item.importedFromListingId) return { listingId: item.importedFromListingId, version: item.importedFromListingVersion, importedAt: item.importedAtVersion };
  if (item.marketplaceListingId) return { listingId: item.marketplaceListingId, version: item.marketplaceVersion, importedAt: null };
  return null;
}

function Fact({ tip, children, ...rest }) {
  return <Tooltip title={tip} describeChild arrow><Box component="span" {...rest}>{children}</Box></Tooltip>;
}

/** Same on every card: version, marketplace version, and who made it / where from. */
function ItemFacts({ item }) {
  const D = useD();
  const link = marketplaceLink(item);
  const originText = ORIGIN_LABEL[item.origin] || ORIGIN_LABEL.MANUAL;
  const originTip = item.origin === "CLASS" ? "Saved from the content of one of your classes."
    : item.origin === "MARKETPLACE" ? "Imported from the marketplace." : "Created directly in the Knowledge Bank.";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap", fontSize: "12px", color: D.ink3 }}>
      <Fact tip="Version of this item in your Knowledge Bank. It goes up each time you edit it." data-testid="fact-version">Version {item.contentVersion ?? 1}</Fact>
      {link && (
        <Fact tip={link.importedAt != null ? "Latest marketplace version, and the version your copy was imported from." : "Version currently on the marketplace."} data-testid="fact-marketplace">
          {link.version != null
            ? `Marketplace v${link.version}${link.importedAt != null ? ` (yours is from v${link.importedAt})` : ""}`
            : "No longer on the marketplace"}
        </Fact>
      )}
      <Fact tip={originTip} data-testid="fact-origin">
        {originText}{item.createdByName ? ` · by ${item.createdByName}` : ""}
      </Fact>
    </Box>
  );
}

function ActionBtn({ children, onClick, primary, title, "aria-label": ariaLabel, "aria-haspopup": ariaHasPopup }) {
  const D = useD();
  const btn = (
    <Box component="button" onClick={onClick} aria-label={ariaLabel} aria-haspopup={ariaHasPopup}
      sx={{ height: 30, px: "10px", borderRadius: "8px", border: `1.5px solid ${primary ? D.primary : D.line}`, bgcolor: primary ? D.primary : "var(--color-surface-canvas-light)", fontFamily: "inherit", fontSize: "11.5px", fontWeight: 700, color: primary ? "var(--color-surface-canvas-light)" : D.ink2, cursor: "pointer", whiteSpace: "nowrap", "&:hover": { bgcolor: primary ? D.primary700 : D.surface3, color: primary ? "var(--color-surface-canvas-light)" : D.ink } }}>
      {children}
    </Box>
  );
  return title ? <Tooltip title={title} describeChild arrow>{btn}</Tooltip> : btn;
}

/** The publishing buttons for one card: Publish | Unpublish + List on marketplace / Marketplace menu. */
function PublishActions({ item, pub }) {
  const [anchor, setAnchor] = useState(null);
  if (item.isSystem) return null;
  const stop = (fn) => (e) => { e.stopPropagation(); fn(e); };
  const listed = !!item.marketplaceListingId;
  return (
    <>
      {item.status === "DRAFT" && <ActionBtn primary title="Publish so your training center can add it to classes" onClick={stop(() => pub.publish(item.id))}>Publish</ActionBtn>}
      {item.status === "PUBLISHED" && <ActionBtn title="Move back to Draft: it can't be added to new classes, and it comes off the marketplace" onClick={stop(() => pub.askUnpublish(item))}>Unpublish</ActionBtn>}
      {item.status === "PUBLISHED" && !listed && !item.importedFromListingId && <ActionBtn title="Share this with other training centers on the marketplace" onClick={stop(() => pub.list(item.id))}>List on marketplace</ActionBtn>}
      {item.status === "PUBLISHED" && listed && (
        <>
          <ActionBtn aria-haspopup="menu" title="Update your marketplace listing or take it down" onClick={stop((e) => setAnchor(e.currentTarget))}>Marketplace ▾</ActionBtn>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} onClick={(e) => e.stopPropagation()}>
            <MenuItem onClick={() => { setAnchor(null); pub.askRepublish(item); }}>Update listing…</MenuItem>
            <MenuItem onClick={() => { setAnchor(null); pub.withdraw(item.id); }}>Remove from marketplace</MenuItem>
          </Menu>
        </>
      )}
    </>
  );
}

/** The same buttons, in the same order, on every card: Publish/Unpublish, Edit, Delete, View in Marketplace. */
function CardActions({ item, pub, onEdit, onDelete }) {
  const stop = (fn) => (e) => { e.stopPropagation(); fn(e); };
  const link = marketplaceLink(item);
  return (
    <>
      <PublishActions item={item} pub={pub} />
      <IconBtn title="Edit" aria-label={`Edit ${item.title}`} onClick={stop(onEdit)}><IcoEdit /></IconBtn>
      <IconBtn danger title="Delete" aria-label={`Delete ${item.title}`} onClick={stop(onDelete)}><IcoTrash /></IconBtn>
      {link && <IconBtn title="View in Marketplace" aria-label={`View ${item.title} in Marketplace`} onClick={stop(() => pub.view(item))}><IcoEye /></IconBtn>}
    </>
  );
}

function DraftBadge() {
  const D = useD();
  return <Box component="span" sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".05em", px: "8px", py: "3px", borderRadius: "6px", bgcolor: D.amber50, color: D.amber, textTransform: "uppercase" }}>Draft</Box>;
}

// ── Section header ────────────────────────────────────────────────────────────
function SecHead({ title, subtitle, btnLabel, btnIcon, onBtn, onImport, importDisabled }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-end", gap: "16px", flexWrap: "wrap", mb: "20px" }}>
      <Box sx={{ flex: 1, minWidth: 240 }}>
        <Typography component="h2" sx={{ fontSize: "22px", fontWeight: 600, letterSpacing: "-.01em", color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>{title}</Typography>
        {subtitle && <Box sx={{ fontSize: "14px", color: D.ink3, mt: "4px" }}>{subtitle}</Box>}
      </Box>
      <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {onImport && <Btn disabled={importDisabled} onClick={onImport} icon={<IcoUpload />}>Import</Btn>}
        {btnLabel && <Btn primary onClick={onBtn} icon={btnIcon}>{btnLabel}</Btn>}
      </Box>
    </Box>
  );
}

// ── Catalog helpers: the facets, sorts and row pieces every bank tab shares ───
const catOf = (i) => i.category || parseTags(i.tags)[0] || "Other";
const statusOf = (i) => (i.status === "DRAFT" ? "Draft" : "Published");
const sourceOf = (i) => ORIGIN_LABEL[i.origin] || ORIGIN_LABEL.MANUAL;
const BASE_FACETS = [
  { key: "cat", title: "Category", fn: catOf },
  { key: "status", title: "Status", fn: statusOf, order: ["Published", "Draft"] },
  { key: "src", title: "Source", fn: sourceOf, order: Object.values(ORIGIN_LABEL) },
];
const byName = ["name", "Name", (a, b) => (a.title || "").localeCompare(b.title || "")];
const byCategory = ["cat", "Category", (a, b) => catOf(a).localeCompare(catOf(b)) || (a.title || "").localeCompare(b.title || "")];
const draftsFirst = ["draft", "Drafts first", (a, b) => (a.status === "DRAFT" ? 0 : 1) - (b.status === "DRAFT" ? 0 : 1) || (a.title || "").localeCompare(b.title || "")];
const searchText = (i) => `${i.title || ""} ${catOf(i)} ${i.description || ""}`;

/** Category + status chips on the top line of a row. */
function RowTop({ item, pub }) {
  return <><CategoryPill label={catOf(item)} /><StatusChips item={item} onView={pub.view} /></>;
}

// ── Inline create form ────────────────────────────────────────────────────────
// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ icon, title, sub }) {
  return (
    <Box sx={{ bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${D.line}`, borderRadius: "15px", p: "48px 24px", textAlign: "center", boxShadow: D.shadowSm }}>
      <Box sx={{ width: 54, height: 54, borderRadius: "14px", bgcolor: D.surface3, color: D.ink4, display: "grid", placeItems: "center", mx: "auto", mb: "14px" }}>{icon}</Box>
      <Box sx={{ fontSize: "15px", fontWeight: 700, color: D.ink2 }}>{title}</Box>
      <Box sx={{ fontSize: "13px", color: D.ink3, mt: "4px" }}>{sub}</Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── SKILL SHEETS TAB ─────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

// Lets any tab raise a snackbar owned by the page.
const ToastCtx = createContext(() => {});

/**
 * Everything about publishing one bank item type:
 *   publish / askUnpublish (Published -> Draft, leaves the marketplace too) /
 *   list / withdraw / askRepublish (already listed: update the listing or publish a copy as a new item).
 * `dialogs` is the JSX for the two confirmations; render it once in the tab.
 */
function useBankPublishing(type, api, setItems, setErr, reload) {
  const notify = useContext(ToastCtx);
  const [unpublishTarget, setUnpublishTarget] = useState(null);
  const [republishTarget, setRepublishTarget] = useState(null);
  const [viewListingId, setViewListingId] = useState(null);
  const fail = (e, fallback) => setErr(e?.response?.data?.message || fallback);
  const patch = (id, changes) => setItems(p => p.map(i => i.id === id ? { ...i, ...changes } : i));

  return {
    publish: async (id) => {
      try {
        const updated = await api.publish(id);
        setItems(p => p.map(i => i.id === id ? updated : i));
        notify("Published to your Knowledge Base.");
      } catch (e) { fail(e, "Failed to publish."); }
    },
    askUnpublish: setUnpublishTarget,
    list: async (id) => {
      try {
        const listing = await publishToMarketplace(type, id);
        patch(id, { marketplaceListingId: listing.id, marketplaceUpdateAvailable: false });
        notify("Published to the marketplace.");
      } catch (e) { fail(e, "Failed to publish to marketplace."); }
    },
    withdraw: async (id) => {
      try {
        await withdrawFromMarketplace(type, id);
        patch(id, { marketplaceListingId: null, marketplaceUpdateAvailable: false });
        notify("Removed from the marketplace.");
      } catch (e) { fail(e, "Failed to remove from the marketplace."); }
    },
    askRepublish: setRepublishTarget,
    view: (item) => setViewListingId(marketplaceLink(item)?.listingId ?? null),
    dialogs: (
      <>
        <MarketplaceListingDialog
          listingId={viewListingId}
          onClose={() => setViewListingId(null)}
          onUpdated={() => { notify("Updated from the marketplace."); reload && reload(); }}
        />
        <ConfirmDialog
          open={!!unpublishTarget}
          title="Unpublish?"
          message={<>
            <b>“{unpublishTarget?.title}”</b> goes back to Draft, so it can't be added to new classes. Classes that already use it keep
            their copies.{unpublishTarget?.marketplaceListingId ? " It will also be removed from the marketplace." : ""}
          </>}
          confirmLabel="Unpublish"
          busyLabel="Unpublishing…"
          danger={false}
          onConfirm={async () => {
            const updated = await api.unpublish(unpublishTarget.id);
            setItems(p => p.map(i => i.id === unpublishTarget.id ? updated : i));
            notify(updated?.listingWithdrawn ? "Unpublished. Its marketplace listing was withdrawn." : "Unpublished — back to Draft.");
          }}
          onClose={() => setUnpublishTarget(null)}
        />
        <RepublishDialog
          item={republishTarget}
          onClose={() => setRepublishTarget(null)}
          onUpdate={async () => {
            await updateMarketplaceListing(type, republishTarget.id);
            patch(republishTarget.id, { marketplaceUpdateAvailable: false });
            notify("Marketplace listing updated.");
          }}
          onNewCopy={async () => {
            const copy = await api.duplicate(republishTarget.id);
            setItems(p => [copy, ...p]);
            notify(`Created “${copy.title}” as a draft. Publish it to list it separately.`);
          }}
        />
      </>
    ),
  };
}

/** "Already on the marketplace": update that listing, or publish a copy as a separate new item. */
function RepublishDialog({ item, onClose, onUpdate, onNewCopy }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (item) { setBusy(false); setError(null); } }, [item]);
  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try { await fn(); onClose(); }
    catch (e) { setError(e?.response?.data?.message || "Something went wrong. Please try again."); setBusy(false); }
  };
  return (
    <Dialog open={!!item} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Already on the marketplace</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <b>“{item?.title}”</b> is already listed. Update that listing with your latest changes, or publish a copy as a
          separate new item — your existing listing stays as it is.
        </DialogContentText>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="outlined" onClick={() => run(onNewCopy)} disabled={busy}>Publish as new copy</Button>
        <Button variant="contained" onClick={() => run(onUpdate)} disabled={busy}>Update listing</Button>
      </DialogActions>
    </Dialog>
  );
}

function SkillSheetsTab({ canWrite, onImport, importDisabled }) {
  const D = useD();
  const notify = useContext(ToastCtx);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    fetchSkillSheetBank()
      .then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch((e) => { setErr(e?.response?.status === 403 ? "You do not have access to this bank in the selected training center." : e?.response?.data?.message || "We couldn't load this right now. Please try again."); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleBuilderSave = async ({ title, description, passingScore, timeLimitMin, scenarioContext, equipment, category, peerAssessmentRequired, items: criteriaItems, tags, savedBankId, requestedStatus }) => {
    const payload = {
      title,
      description,
      passingScore: passingScore ?? null,
      timeLimitMin: timeLimitMin ?? null,
      scenarioContext, equipment, category, peerAssessmentRequired,
      items: JSON.stringify(criteriaItems),
      tags: tags || "[]",
    };
    let item = savedBankId ? await updateSkillSheetBankItem(savedBankId, payload) : await createSkillSheetBankItem(payload);
    setItems(p => [item, ...p.filter((old) => old.id !== item.id)]);
    if (requestedStatus === "PUBLISHED") {
      try {
        item = await publishSkillSheetBankItem(item.id);
        setItems(p => p.map((old) => old.id === item.id ? item : old));
      } catch (cause) {
        const error = new Error("Your draft was saved, but publishing failed. Try Publish again.");
        error.savedBankId = item.id;
        throw error;
      }
    }
    // The builder keeps the id, so later auto-saves update this item rather than creating another.
    return item;
  };

  const handleDelete = async (id) => {
    const res = await deleteSkillSheetBankItem(id);
    setItems(p => p.filter(i => i.id !== id));
    if (res?.listingWithdrawn) notify("Deleted. Its marketplace listing was withdrawn.");
  };

  const pub = useBankPublishing("SKILL_SHEET", {
    publish: publishSkillSheetBankItem,
    unpublish: unpublishSkillSheetBankItem,
    duplicate: duplicateSkillSheetBankItem,
  }, setItems, setErr, load);

  return (
    <Box>
      <SecHead title="Skill Sheet Bank" subtitle="Reusable practical skill checklists — assign to any course."
        btnLabel={canWrite ? "New Skill Sheet" : null} btnIcon={<IcoPlus />}
        onBtn={() => setBuilderOpen(true)}
        onImport={canWrite ? onImport : null} importDisabled={importDisabled} />

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2, borderRadius: "10px" }}>{err}<Button size="small" onClick={load}>Retry</Button></Alert>}

      <SkillSheetBuilderModal
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        bankMode
        onSave={handleBuilderSave}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: D.primary }} /></Box>
      ) : items.length === 0 && !err ? (
        <EmptyState icon={<IcoSkill />} title="No skill sheets yet" sub="Click 'New Skill Sheet' to create your first checklist." />
      ) : (
        <KbCatalog items={items} noun="skill sheet" searchPlaceholder="Search skill sheets by name or category"
          facets={BASE_FACETS} searchText={searchText}
          sorts={[byName, byCategory, draftsFirst, ["steps", "Most steps", (a, b) => parseCount(b.items) - parseCount(a.items)]]}
          renderRow={(item) => {
            const steps = parseCount(item.items);
            const totalPoints = calcTotalPoints(item.items);
            const open = () => navigate(`/instructor/knowledge-bank/skill-sheets/${item.id}`);
            return {
              testId: `kb-row-${item.id}`,
              onOpen: open,
              top: <RowTop item={item} pub={pub} />,
              title: item.title,
              description: item.description,
              facts: <ItemFacts item={item} />,
              meta: <>
                <MetaItem icon={<IcoClock />}>{item.timeLimitMin ? <Box component="span" sx={{ fontWeight: 700, color: D.ink }}>≤ {fmtTime(item.timeLimitMin)}</Box> : "No limit"}</MetaItem>
                {totalPoints != null && <MetaItem icon={<IcoSteps />}>Total <Box component="span" sx={{ fontWeight: 700, color: D.ink }}>{totalPoints}</Box> pts</MetaItem>}
                {item.peerAssessmentRequired && <MetaItem><Box component="span" sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: ".05em", px: "8px", py: "3px", borderRadius: "6px", bgcolor: D.primary50, color: D.primary, textTransform: "uppercase" }}>Peer required</Box></MetaItem>}
              </>,
              figure: <RowFigure value={steps} unit={steps === 1 ? "step" : "steps"} caption={item.passingScore != null ? `Pass ${item.passingScore} pts` : null} />,
              actions: canWrite && !item.isSystem ? <CardActions item={item} pub={pub} onEdit={open} onDelete={() => setDeleteId(item.id)} /> : null,
            };
          }} />
      )}

      {pub.dialogs}
      <ConfirmDialog
        open={!!deleteId}
        title="Delete skill sheet?"
        message={<>This permanently removes <b>“{items.find(i => i.id === deleteId)?.title}”</b>. It can't be deleted while a class uses it.</>}
        confirmLabel="Delete skill sheet"
        onConfirm={() => handleDelete(deleteId)}
        onClose={() => setDeleteId(null)}
      />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── TESTS TAB ────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function TestsTab({ canWrite, onImport, importDisabled }) {
  const D = useD();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchTestBank()
      .then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch((e) => { setErr(e?.response?.status === 403 ? "You do not have access to this bank in the selected training center." : e?.response?.data?.message || "We couldn't load this right now. Please try again."); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleModalSave = async ({ title, description, passingScore, timeLimitMinutes, maxRetakes, questions, tags, enabled, savedBankId }) => {
    const payload = {
      title,
      description,
      passingScore: passingScore ?? null,
      timeLimitMin: timeLimitMinutes ?? null,
      maxRetakes: maxRetakes ?? 0,
      questions: JSON.stringify(Array.isArray(questions) ? questions : []),
      tags: tags || "[]",
      enabled: !!enabled,
    };
    // An auto-saved test already exists: update it instead of creating a second copy.
    const item = savedBankId ? await updateTestBankItem(savedBankId, payload) : await createTestBankItem(payload);
    setItems(p => [item, ...p.filter((old) => old.id !== item.id)]);
    return item;
  };

  const handleDelete = async (id) => {
    await deleteTestBankItem(id);
    setItems(p => p.filter(i => i.id !== id));
  };

  const pub = useBankPublishing("TEST", {
    publish: publishTestBankItem,
    unpublish: unpublishTestBankItem,
    duplicate: duplicateTestBankItem,
  }, setItems, setErr, load);

  return (
    <Box>
      <SecHead title="Test Bank" subtitle="Reusable written exams and quizzes — assign to any course."
        btnLabel={canWrite ? "New Test" : null} btnIcon={<IcoPlus />}
        onBtn={() => setModalOpen(true)}
        onImport={canWrite ? onImport : null} importDisabled={importDisabled} />

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2, borderRadius: "10px" }}>{err}<Button size="small" onClick={load}>Retry</Button></Alert>}

      <CreateTestModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleModalSave}
        bank
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: D.primary }} /></Box>
      ) : items.length === 0 ? (
        <EmptyState icon={<IcoTest />} title="No tests yet" sub="Click 'New Test' to create your first test." />
      ) : (
        <KbCatalog items={items} noun="test" searchPlaceholder="Search tests by name or category"
          facets={BASE_FACETS} searchText={searchText}
          sorts={[byName, byCategory, draftsFirst, ["q", "Most questions", (a, b) => parseCount(b.questions) - parseCount(a.questions)]]}
          renderRow={(item) => {
            const qCount = parseCount(item.questions);
            const open = () => navigate(`/instructor/knowledge-bank/tests/${item.id}`);
            return {
              testId: `kb-row-${item.id}`,
              onOpen: open,
              top: <RowTop item={item} pub={pub} />,
              title: item.title,
              description: item.description,
              facts: <ItemFacts item={item} />,
              meta: <MetaItem icon={<IcoClock />}>{item.timeLimitMin ? <><Box component="span" sx={{ fontWeight: 700, color: D.ink }}>{item.timeLimitMin}</Box> min</> : "No time limit"}</MetaItem>,
              figure: <RowFigure value={qCount} unit={qCount === 1 ? "question" : "questions"} caption={item.passingScore != null ? `Pass ${item.passingScore}%` : null} />,
              actions: canWrite && !item.isSystem ? <CardActions item={item} pub={pub} onEdit={open} onDelete={() => setDeleteId(item.id)} /> : null,
            };
          }} />
      )}

      {pub.dialogs}
      <ConfirmDialog
        open={!!deleteId}
        title="Delete test?"
        message={<>This permanently removes <b>“{items.find(i => i.id === deleteId)?.title}”</b>.</>}
        confirmLabel="Delete test"
        onConfirm={() => handleDelete(deleteId)}
        onClose={() => setDeleteId(null)}
      />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── DOCUMENTS TAB ────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function DocumentsTab({ canWrite, onImport, importDisabled }) {
  const D = useD();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchDocumentBank()
      .then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch((e) => { setErr(e?.response?.status === 403 ? "You do not have access to this bank in the selected training center." : e?.response?.data?.message || "We couldn't load this right now. Please try again."); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUploadSave = async (formData) => {
    const payload = {
      title: formData.title || '',
      description: formData.description || '',
      contentType: formData.contentType || (formData.file ? 'FILE' : 'LINK'),
      url: formData.url || '',
      embedCode: formData.embedCode || '',
      tags: Array.isArray(formData.tags) ? formData.tags : [],
      required: !!formData.required,
    };
    const fd = new FormData();
    fd.append('data', JSON.stringify(payload));
    if (formData.file) fd.append('file', formData.file);
    await createDocumentBankItem(fd);
    await load();
    setUploadOpen(false);
  };

  const handleDelete = async (id) => {
    await deleteDocumentBankItem(id);
    setItems(p => p.filter(i => i.id !== id));
  };

  const pub = useBankPublishing("DOCUMENT", {
    publish: publishDocumentBankItem,
    unpublish: unpublishDocumentBankItem,
    duplicate: duplicateDocumentBankItem,
  }, setItems, setErr, load);

  return (
    <Box>
      <SecHead title="Document Library" subtitle="Reference materials, manuals, and handouts — attach to any course."
        btnLabel={canWrite ? "Upload Document" : null} btnIcon={<IcoUpload />}
        onBtn={() => setUploadOpen(true)}
        onImport={canWrite ? onImport : null} importDisabled={importDisabled} />

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2, borderRadius: "10px" }}>{err}<Button size="small" onClick={load}>Retry</Button></Alert>}

      <AddClassContentModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSave={handleUploadSave}
        sessions={[]}
        dialogTarget="class"
        titleOverride="Add to Document Bank"
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: D.primary }} /></Box>
      ) : items.length === 0 ? (
        <EmptyState icon={<IcoDoc />} title="No documents yet" sub="Click 'Upload Document' to add your first file." />
      ) : (
        <KbCatalog items={items} noun="document" searchPlaceholder="Search documents by name or category"
          facets={[...BASE_FACETS, { key: "type", title: "File type", fn: (i) => (ftFromFileName(i.fileName, i.fileContentType) || i.contentType || "").toUpperCase() }]}
          searchText={searchText}
          sorts={[byName, byCategory, draftsFirst, ["size", "Largest first", (a, b) => (b.fileSize || 0) - (a.fileSize || 0)]]}
          renderRow={(item) => {
            const ft = ftFromFileName(item.fileName, item.fileContentType);
            const open = () => navigate(`/instructor/knowledge-bank/documents/${item.id}`);
            return {
              testId: `kb-row-${item.id}`,
              onOpen: open,
              top: <RowTop item={item} pub={pub} />,
              title: item.title,
              description: item.description,
              facts: <ItemFacts item={item} />,
              meta: <>
                {ft && <FtBadge ft={ft} />}
                {item.contentType && !ft && <Box sx={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: D.ink4 }}>{item.contentType}</Box>}
              </>,
              figure: item.fileSize ? <RowFigure value={fmtSize(item.fileSize)} /> : null,
              actions: canWrite && !item.isSystem ? <CardActions item={item} pub={pub} onEdit={open} onDelete={() => setDeleteId(item.id)} /> : null,
            };
          }} />
      )}

      {pub.dialogs}
      <ConfirmDialog
        open={!!deleteId}
        title="Delete document?"
        message={<>This permanently removes <b>“{items.find(i => i.id === deleteId)?.title}”</b>.</>}
        confirmLabel="Delete document"
        onConfirm={() => handleDelete(deleteId)}
        onClose={() => setDeleteId(null)}
      />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── CLASS TEMPLATES TAB ──────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
/** Class templates have no detail page, so their editable basics are edited here. */
function TemplateEditDialog({ item, onClose, onSave }) {
  const [form, setForm] = useState({ title: "", description: "", courseNumber: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // The certificates classes made from this template start with (coordinators/admins only; hidden otherwise).
  const [certs, setCerts] = useState(null);
  const [chosenCerts, setChosenCerts] = useState([]);
  useEffect(() => {
    if (item) { setForm({ title: item.title || "", description: item.description || "", courseNumber: item.courseNumber || "" }); setBusy(false); setError(null); }
    setCerts(null);
    if (!item) return undefined;
    let active = true;
    fetchTemplateCertificateTypes(item.id)
      .then((d) => { if (active) { setCerts(d); setChosenCerts(d?.types || []); } })
      .catch(() => { if (active) setCerts(null); });
    return () => { active = false; };
  }, [item]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true); setError(null);
    try {
      await onSave(form);
      if (certs && JSON.stringify(chosenCerts) !== JSON.stringify(certs.types || [])) {
        await saveTemplateCertificateTypes(item.id, chosenCerts);
      }
      onClose();
    }
    catch (e) { setError(e?.response?.data?.message || "Couldn't save the template."); setBusy(false); }
  };
  return (
    <Dialog open={!!item} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit class template</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <TextField label="Title" value={form.title} onChange={set("title")} required fullWidth />
        <TextField label="Course number" value={form.courseNumber} onChange={set("courseNumber")} fullWidth />
        <TextField label="Description" value={form.description} onChange={set("description")} multiline minRows={3} fullWidth />
        {certs && (
          <CertificateTypesPicker available={certs.available || []} value={chosenCerts} onChange={setChosenCerts}
            disabled={busy} label="Certificates for classes made from this template" />
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !form.title.trim()}>{busy ? "Saving…" : "Save"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function ClassTemplatesTab({ tenantId }) {
  const D = useD();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [instantiating, setInstantiating] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(() => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    fetchClassTemplates(tenantId)
      .then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch((e) => { setErr(e?.response?.status === 403 ? "You do not have access to this bank in the selected training center." : e?.response?.data?.message || "We couldn't load this right now. Please try again."); setLoading(false); });
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleUse = async (id) => {
    setInstantiating(id);
    try {
      const created = await instantiateClassTemplate(tenantId, id);
      navigate(`/course-hub/${created.id}`);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to create a class from this template.");
      setInstantiating(null);
    }
  };

  const pub = useBankPublishing("CLASS_TEMPLATE", {
    publish: (id) => publishClassTemplate(tenantId, id),
    unpublish: (id) => unpublishClassTemplate(tenantId, id),
    duplicate: (id) => duplicateClassTemplate(tenantId, id),
  }, setItems, setErr, load);

  return (
    <Box>
      <SecHead title="Class Templates" subtitle="Reusable class structures — sessions and attached content, no roster — create a new class from one." />

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2, borderRadius: "10px" }}>{err}<Button size="small" onClick={load}>Retry</Button></Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: D.primary }} /></Box>
      ) : items.length === 0 ? (
        <EmptyState icon={<IcoCourse />} title="No class templates yet" sub="Save a class as a template from Course Management to see it here." />
      ) : (
        <KbCatalog items={items} noun="class template" searchPlaceholder="Search class templates by name or category"
          facets={BASE_FACETS} searchText={(i) => `${searchText(i)} ${i.courseNumber || ""}`}
          sorts={[byName, byCategory, draftsFirst]}
          renderRow={(item) => ({
            testId: `kb-row-${item.id}`,
            top: <RowTop item={item} pub={pub} />,
            title: item.title,
            description: item.description,
            facts: <ItemFacts item={item} />,
            meta: <MetaItem icon={<IcoSteps />}>{item.courseNumber || "No course #"}</MetaItem>,
            actions: <>
              {!item.isSystem && (
                <CardActions item={item} pub={pub} onEdit={() => setEditTarget(item)} onDelete={() => setDeleteId(item.id)} />
              )}
              <Tooltip title={item.status && item.status !== "PUBLISHED" ? "Publish the template first" : "Create a new class from this template"} describeChild arrow>
                <span>
                  <Btn sm primary onClick={() => handleUse(item.id)} disabled={instantiating === item.id || (!!item.status && item.status !== "PUBLISHED")}>
                    {instantiating === item.id ? "Creating…" : "Use Template"}
                  </Btn>
                </span>
              </Tooltip>
            </>,
          })} />
      )}

      {pub.dialogs}
      <TemplateEditDialog
        item={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={async (changes) => {
          const updated = await updateClassTemplate(tenantId, editTarget.id, changes);
          setItems(p => p.map(i => i.id === editTarget.id ? updated : i));
        }}
      />
      <ConfirmDialog
        open={!!deleteId}
        title="Delete class template?"
        message={<>This permanently removes the template <b>“{items.find(i => i.id === deleteId)?.title}”</b>. Classes already created from it are not affected.</>}
        confirmLabel="Delete template"
        onConfirm={async () => {
          await deleteClassTemplate(tenantId, deleteId);
          setItems(p => p.filter(i => i.id !== deleteId));
        }}
        onClose={() => setDeleteId(null)}
      />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── ROOT PAGE ────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
const TAB_FOR_TYPE = { TEST: "tests", SKILL_SHEET: "skills", DOCUMENT: "docs", CLASS_TEMPLATE: "classes" };

export default function InstructorKnowledgeBankPage() {
  const D = useD();
  const { user } = useTenant();
  const { tenantId: activeTenantId, isGlobalAdmin } = useActiveTenant();
  const canWrite = user?.roles?.includes("COURSE_COORDINATOR") || isGlobalAdmin;

  const tabs = [
    { key: "skills",  label: "Skill Sheets", icon: <IcoSkill /> },
    { key: "tests",   label: "Tests",        icon: <IcoTest /> },
    { key: "docs",    label: "Documents",    icon: <IcoDoc /> },
    ...(canWrite ? [{ key: "classes", label: "Class Templates", icon: <IcoCourse /> }] : []),
    ...(canWrite ? [{ key: "standard", label: "Standard", icon: <IcoCourse /> }] : []),
  ];

  const navigate = useNavigate();
  const location = useLocation();
  // /instructor/knowledge-bank?tab=standard opens the Standard tab (the class-creation page links here).
  const [activeTab, setActiveTab] = useState(() => (canWrite && new URLSearchParams(location.search).get("tab") === "standard" ? "standard" : "skills"));
  const [importOpen, setImportOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState(null);
  const notify = useCallback((message, severity = "success") => setToast({ message, severity }), []);

  // Picked up after navigating back from the Marketplace page (a real route, not a modal) —
  // see MarketplaceView's goBack(), which hands back everything imported during that visit.
  useEffect(() => {
    const imports = location.state?.imports;
    if (!imports || imports.length === 0) return;
    const last = imports[imports.length - 1];
    setActiveTab(TAB_FOR_TYPE[last.type] || activeTab);
    setRefreshKey((k) => k + 1);
    notify(imports.length === 1
      ? `Imported "${last.title}" into your knowledge bank as a draft.`
      : `Imported ${imports.length} items into your knowledge bank as drafts.`);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const tabKey = `${isGlobalAdmin ? (activeTenantId ?? "none") : "default"}-${refreshKey}`;

  return (
    <ToastCtx.Provider value={notify}>
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: "4px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" }}>
        <Box>
          <Box sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: D.primary, mb: "8px" }}>Instructor Dashboard</Box>
          <Typography sx={{ fontSize: { xs: "28px", md: "34px" }, fontWeight: 600, letterSpacing: "-.01em", color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>Knowledge Bank</Typography>
          <Box sx={{ fontSize: "14.5px", color: D.ink3, mt: "6px" }}>Build reusable skill sheets, tests, documents, and class templates — assign them to any course.</Box>
        </Box>
        <Box sx={{ display: "flex", gap: "10px", flexShrink: 0, mt: "4px" }}>
          <Button variant="outlined" onClick={() => navigate("/instructor/knowledge-bank/marketplace")}>
            View Marketplace
          </Button>
        </Box>
      </Box>

      {/* Tab bar */}
      <Box sx={{ display: "flex", gap: 0, borderBottom: `1px solid ${D.line}`, mt: "22px", mb: "0" }}>
        {tabs.map(tab => (
          <Box key={tab.key} component="button" onClick={() => setActiveTab(tab.key)}
            sx={{ height: 46, px: "18px", border: 0, borderBottom: `2.5px solid ${activeTab === tab.key ? D.primary : "transparent"}`, bgcolor: "transparent", fontFamily: "inherit", fontSize: "14px", fontWeight: 600, color: activeTab === tab.key ? D.primary : D.ink3, cursor: "pointer", transition: "color .13s,border-color .13s", display: "inline-flex", alignItems: "center", gap: "9px", mb: "-1px", "&:hover": { color: D.ink } }}>
            {tab.icon}
            {tab.label}
          </Box>
        ))}
      </Box>

      {/* Guard: global admins must have a tenant selected before tabs load */}
      {isGlobalAdmin && !activeTenantId ? (
        <Box sx={{ mt: "40px" }}>
          <NoTenantSelected message="Select a training center from the top bar to view its knowledge bank." />
        </Box>
      ) : (
        /* key forces remount when tenant changes so tabs reload their data */
        <Box key={tabKey} sx={{ pt: "24px" }}>
          {activeTab === "skills"  && <SkillSheetsTab canWrite={canWrite} onImport={() => setImportOpen(true)} importDisabled={isGlobalAdmin && !activeTenantId} />}
          {activeTab === "tests"   && <TestsTab canWrite={canWrite} onImport={() => setImportOpen(true)} importDisabled={isGlobalAdmin && !activeTenantId} />}
          {activeTab === "docs"    && <DocumentsTab canWrite={canWrite} onImport={() => setImportOpen(true)} importDisabled={isGlobalAdmin && !activeTenantId} />}
          {activeTab === "classes" && canWrite && <ClassTemplatesTab tenantId={activeTenantId} />}
          {activeTab === "standard" && canWrite && <StandardCoursesTab tenantId={activeTenantId} />}
        </Box>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => setRefreshKey((k) => k + 1)}
      />

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">{toast.message}</Alert> : undefined}
      </Snackbar>
    </Box>
    </ToastCtx.Provider>
  );
}
